import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import React, { lazy, Suspense } from "react";
import SwiperCore, { Mousewheel, Scrollbar } from "swiper";
import { SwiperSlide } from "swiper/react";
import tw, { styled } from "twin.macro";
import { Icon } from "../../Icon";
import { Image } from "../../Image/Image";
import { TouchDeviceProps } from "./Images";
import { ImagesOutputProps } from "./withImages";

const Swiper = lazy(
  () => import(/* webpackChunkName: "swiper" */ "../../Swiper/Swiper"),
);

SwiperCore.use([Scrollbar, Mousewheel]);

type ThumbnailProps = TouchDeviceProps & {
  active?: boolean;
  loaded?: boolean;
};

const ThumbnailContainer = styled.aside`
  ${tw`w-full lg:overflow-hidden`}
  max-height: ${({ height }) => (height ? `${height}px` : "auto")};
  .swiper,
  .swiper-wrapper {
    max-height: ${({ height }) => (height ? `${height}px` : "auto")};
  }
`;

const Thumbnail = styledWithInvalidProps("button")(
  ({ active, loaded, isTouchDevice }: ThumbnailProps) => [
    tw`relative w-full transition-opacity border border-transparent border-solid mt-mini ease-cubic mb-mini focus:outline-none focus:border-quaternary focus:border-solid focus:border lg:mt-0 lg:last:mb-0 lg:w-9/10 motion-reduce:transition-none`,
    active
      ? tw`border border-solid opacity-100 border-quaternary`
      : tw`opacity-60`,
    loaded ? null : tw`opacity-100 bg-tertiary bg-opacity-60`,
    isTouchDevice ? tw`lg:mt-mini lg:w-full` : null,
  ],
);

const IconWrapper = tw.button`
  absolute inset-0 w-full h-full flex items-center justify-center z-10 focus:outline-none
`;

export const Thumbnails = ({
  images,
  height,
  isTouchDevice,
  loaded,
  onImagesReady,
  onThumbnailClick,
  onSwiperInit,
  onSlideChange,
  slideIndex,
}: ImagesOutputProps) => (
  <ThumbnailContainer height={height}>
    <Suspense fallback={<></>}>
      <Swiper
        direction="horizontal"
        observeParents={true}
        observer={true}
        onImagesReady={onImagesReady}
        onSlideChange={onSlideChange}
        onSwiper={onSwiperInit}
        slidesPerView={5}
        spaceBetween={10}
        scrollbar={{ draggable: true }}
        mousewheel={true}
        breakpoints={{
          1024: !isTouchDevice
            ? {
                allowTouchMove: false,
                direction: "vertical",
                height,
                observeParents: true,
                observer: true,
                scrollbar: { draggable: true },
                slidesPerView: "auto",
                spaceBetween: 8,
              }
            : null,
        }}
      >
        {images?.map((image, index: number) => (
          <SwiperSlide key={index}>
            <Thumbnail
              loaded={loaded}
              onClick={onThumbnailClick(index)}
              active={slideIndex === index}
              isTouchDevice={isTouchDevice}
            >
              {image?.isCoverImage && (
                <IconWrapper>
                  <Icon name="play" size="small" />
                </IconWrapper>
              )}
              <Image
                alt={image.alt}
                cover={true}
                loading="eager"
                image={image.src || image.url}
                ratio="12/13"
                maxWidth={200}
              />
            </Thumbnail>
          </SwiperSlide>
        ))}
      </Swiper>
    </Suspense>
  </ThumbnailContainer>
);
